import * as React from 'react';
import styled from 'styled-components';

const FallbackBox = styled.div`
	padding: ${p => p.theme.sys.spacing[300]};
	${p => p.theme.sys.font?.body?.['2']}
	color: ${p => p.theme.sys.color.text.semantic.error};
`;

interface Props {
	children: React.ReactNode;
	/** Shown in place of the children once they have thrown. */
	fallbackMessage?: string;
}

interface State {
	error: Error | null;
}

/**
 * Contains a render error raised by a single picker's option list.
 *
 * ForecastQueryRenderer nests Forecast's app-level ErrorBoundary inside the
 * ticketing route boundaries, so an uncaught throw while rendering a picker
 * takes the whole page to the app error screen rather than the route's own
 * message. Wrapping just the option list keeps a malformed candidate local: the
 * trigger and the search box stay usable and the rest of the page survives.
 *
 * Note this only catches throws from its children, so the list rendering — and
 * the filtering it does — has to live in a child component, not in the picker
 * that renders this boundary.
 */
export class PickerErrorBoundary extends React.Component<Props, State> {
	override state: State = {error: null};

	static getDerivedStateFromError(error: Error): State {
		return {error};
	}

	override render() {
		if (this.state.error) {
			return <FallbackBox role="alert">{this.props.fallbackMessage ?? 'This list could not be shown.'}</FallbackBox>;
		}
		return this.props.children;
	}
}
